import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, Select, Input, message, Space, Radio, Spin, Tag, Tooltip, Empty, Alert, Typography, Button } from "antd";
import { SearchOutlined, InfoCircleOutlined, RocketOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { uldService, flightOperationFlightService } from "../../../../api/enhancedApi";
import { ULD_LOCATION_TYPE, ULD_LOCATION_TYPE_NAMES } from "../constants/uldConstants";
import { FLIGHT_STATUS_LABELS, FLIGHT_STATUS_COLORS } from "../../../flightOperation/Monitor/constants/flightConstants";
import dayjs from "dayjs";
import { useUldAirports } from "../../hooks/useUldAirports";

const { Option } = Select;
const { Text } = Typography;
const MOVEMENT_REASON_MAX_LENGTH = 255;
const LOCATION_LABEL_MAX_LENGTH = 60;
const USER_NOTE_MAX_LENGTH = 160;

const compactLabel = (value, fallback) => {
    const text = String(value || fallback);
    return text.length > LOCATION_LABEL_MAX_LENGTH
        ? `${text.slice(0, LOCATION_LABEL_MAX_LENGTH - 3)}...`
        : text;
};

const UldLocationUpdateModal = ({ 
    visible, 
    onCancel, 
    onSuccess, 
    record 
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [locationType, setLocationType] = useState(ULD_LOCATION_TYPE.AIRPORT);
    
    // 走 ULD 内部的 useUldAirports（/v1/airports，仅需登录），避免 schedule:read 权限阻塞
    const { airportsList, loading: airportsLoading } = useUldAirports();
    
    // 航班选择相关状态
    const [flightSelectionMode, setFlightSelectionMode] = useState('existing'); // 'existing' | 'manual'
    const [flightSearchLoading, setFlightSearchLoading] = useState(false);
    const [flightOptions, setFlightOptions] = useState([]);
    const [flightSearchKeyword, setFlightSearchKeyword] = useState('');

    // 【新增】位置一致性校验错误状态
    const [consistencyError, setConsistencyError] = useState(null);
    const [forceUpdate, setForceUpdate] = useState(false);

    useEffect(() => {
        if (visible && record) {
            // 【修复】每次打开Modal时清理状态
            setConsistencyError(null);
            setForceUpdate(false);
            setFlightOptions([]);
            setFlightSearchKeyword('');
            
            // 初始化表单
            const initialValues = {
                location_type: record.stock?.current_location_type,
                airport_iata: record.stock?.current_airport_iata,
                flight_op_id: record.stock?.current_flight_op_id,
                transport_identifier: record.stock?.transport_identifier,
                new_other_location: record.stock?.current_other_location,
                reason: ""
            };
            
            form.setFieldsValue(initialValues);
            setLocationType(record.stock?.current_location_type);
            
            // 根据当前数据设置航班选择模式
            if (record.stock?.current_location_type === ULD_LOCATION_TYPE.FLIGHT) {
                if (record.stock?.current_flight_op_id) {
                    setFlightSelectionMode('existing');
                } else if (record.stock?.transport_identifier) {
                    setFlightSelectionMode('manual');
                }
            }
        }
    }, [visible, record, form]);

    // 表单字段变化时重置位置一致性错误（只在用户主动修改时）
    const handleLocationTypeChange = (value) => {
        setLocationType(value);
        // 只有在有一致性错误时才重置，避免影响错误显示
        if (consistencyError) {
            setConsistencyError(null);
            setForceUpdate(false);
        }
    };

    // 搜索航班数据
    const searchFlights = useCallback(async (keyword = '') => {
        if (!keyword || keyword.length < 2) {
            setFlightOptions([]);
            return;
        }
        
        setFlightSearchLoading(true);
        try {
            // 获取航班数据，使用较大的分页以获取更多选项
            const params = {
                page: 1,
                page_size: 50,
                // 设置时间范围：过去1天到未来7天
                start_date: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
                end_date: dayjs().add(7, 'day').format('YYYY-MM-DD'),
                // 使用关键词搜索（后端支持航班号搜索）
                search_text: keyword,
                // 只获取可能的航班状态
                status: 'SCHEDULED,DEPARTED,DELAYED'
            };

            console.log('搜索航班参数:', params);
            const response = await flightOperationFlightService.getFlightOperations(params);
            
            if (response.data && response.data.items) {
                setFlightOptions(response.data.items);
            } else {
                setFlightOptions([]);
            }
        } catch (error) {
            console.error('搜索航班失败:', error);
            message.error('搜索航班失败');
            setFlightOptions([]);
        } finally {
            setFlightSearchLoading(false);
        }
    }, []);

    // 处理航班搜索输入
    const handleFlightSearch = useCallback((value) => {
        setFlightSearchKeyword(value);
        searchFlights(value);
    }, [searchFlights]);

    // 当选择现有航班时，清空手动输入
    const handleExistingFlightSelect = (value) => {
        form.setFieldsValue({
            flight_op_id: value,
            transport_identifier: undefined
        });
        // 航班选择变化时重置一致性错误
        if (consistencyError) {
            setConsistencyError(null);
            setForceUpdate(false);
        }
    };

    // 当手动输入时，清空现有航班选择
    const handleManualFlightInput = (e) => {
        form.setFieldsValue({
            flight_op_id: undefined,
            transport_identifier: e.target.value
        });
        // 手动输入航班变化时重置一致性错误
        if (consistencyError) {
            setConsistencyError(null);
            setForceUpdate(false);
        }
    };

    const getCurrentLocationLabel = () => {
        const stock = record.stock || {};
        if (stock.current_location_type === ULD_LOCATION_TYPE.AIRPORT) {
            return compactLabel(stock.current_airport_iata, "未知机场");
        }
        if (stock.current_location_type === ULD_LOCATION_TYPE.FLIGHT) {
            const flightRef = record.current_flight_number
                || stock.transport_identifier
                || (stock.current_flight_op_id ? `#${stock.current_flight_op_id}` : "?");
            return compactLabel(`航班${flightRef}`, "未知航班");
        }
        return compactLabel(
            stock.current_other_location || stock.current_location_type,
            "未知位置",
        );
    };

    const getTargetLocationLabel = (values) => {
        if (locationType === ULD_LOCATION_TYPE.AIRPORT) {
            return compactLabel(values.airport_iata, "未知机场");
        }
        if (locationType === ULD_LOCATION_TYPE.FLIGHT) {
            const selectedFlight = flightOptions.find(
                (flight) => String(flight.id) === String(values.flight_op_id),
            );
            const flightRef = selectedFlight?.flight_number
                || values.transport_identifier
                || (values.flight_op_id ? `#${values.flight_op_id}` : "?");
            const departureTime = selectedFlight?.etd || selectedFlight?.std;
            const dateSuffix = departureTime ? `/${dayjs(departureTime).format("MM-DD")}` : "";
            return compactLabel(`航班${flightRef}${dateSuffix}`, "未知航班");
        }
        return compactLabel(values.new_other_location, "其他位置");
    };

    const buildLocationReason = (values) => {
        const autoReason = `${getCurrentLocationLabel()} → ${getTargetLocationLabel(values)}`;
        const userNote = (values.reason || "").trim().slice(0, USER_NOTE_MAX_LENGTH);
        const reason = userNote ? `${autoReason} (${userNote})` : autoReason;
        return reason.slice(0, MOVEMENT_REASON_MAX_LENGTH);
    };

    const handleSubmit = async () => {
        let shouldSetLoadingFalse = true; // 标志位控制是否在finally中设置loading
        
        try {
            const values = await form.validateFields();
            setLoading(true);

            const requestData = {
                location_type: locationType,
                airport_iata: values.airport_iata,
                flight_op_id: values.flight_op_id,
                transport_identifier: values.transport_identifier,
                new_other_location: values.new_other_location,
                force_update: forceUpdate,
                reason: buildLocationReason(values)
            };

            console.log('提交ULD位置更新:', { forceUpdate, requestData });

            const response = await uldService.updateSingleUldLocation(record.master.id, requestData);
            
            // 检查HTTP状态码来判断成功/失败
            if (response.status >= 200 && response.status < 300) {
                message.success("位置更新成功");
                onSuccess?.();
                onCancel();
            } else {
                message.error(response.data?.message || "位置更新失败");
            }
        } catch (error) {
            if (error.errorFields) {
                // 表单验证错误
                message.error("请检查表单填写是否正确");
                return;
            }
            console.error('位置更新失败:', error);
            const errorMessage = error.response?.data?.detail || 
                               error.response?.data?.message || 
                               "位置更新失败";
            
            // 【新增】处理位置一致性校验错误 - 但不在强制更新时处理
            if (error.response && error.response.data && !forceUpdate) {
                const errorData = error.response.data;
                
                // 检查是否为位置一致性错误
                if (errorData.detail && errorData.detail.includes('与航班') && errorData.detail.includes('不一致')) {
                    // 检查是否有额外的错误详情（后端通过Exception args传递）
                    if (errorData.error_details && errorData.error_details.consistency_error) {
                        setConsistencyError(errorData.error_details);
                        message.warning('检测到位置一致性问题，请查看警告信息');
                        shouldSetLoadingFalse = false; // 不在finally中设置loading
                        setLoading(false); // 立即停止loading，让用户看到警告
                        return;
                    }
                    
                    // 如果没有结构化的错误详情，尝试从错误消息中提取信息
                    try {
                        const errorMessage = errorData.detail;
                        let consistencyData = { consistency_error: true };
                        
                        // 解析错误消息，提取关键信息
                        if (errorMessage.includes('ULD当前位置') && errorMessage.includes('与航班始发站')) {
                            // ULD移入航班的错误
                            const currentMatch = errorMessage.match(/ULD当前位置\(([^)]+)\)/);
                            const originMatch = errorMessage.match(/与航班始发站\(([^)]+)\)/);
                            
                            if (currentMatch && originMatch) {
                                consistencyData = {
                                    ...consistencyData,
                                    check_type: "ULD移入航班",
                                    current_airport: currentMatch[1],
                                    expected_airport: originMatch[1],
                                    flight_origin: originMatch[1]
                                };
                            }
                        } else if (errorMessage.includes('目标机场') && errorMessage.includes('与航班目的站')) {
                            // ULD移出航班的错误
                            const targetMatch = errorMessage.match(/目标机场\(([^)]+)\)/);
                            const destinationMatch = errorMessage.match(/与航班目的站\(([^)]+)\)/);
                            
                            if (targetMatch && destinationMatch) {
                                consistencyData = {
                                    ...consistencyData,
                                    check_type: "ULD移出航班",
                                    new_airport: targetMatch[1],
                                    expected_airport: destinationMatch[1],
                                    flight_destination: destinationMatch[1]
                                };
                            }
                        }
                        
                        setConsistencyError(consistencyData);
                        message.warning('检测到位置一致性问题，请查看警告信息');
                        shouldSetLoadingFalse = false; // 不在finally中设置loading
                        setLoading(false); // 立即停止loading，让用户看到警告
                        return;
                    } catch (e) {
                        console.error('解析位置一致性错误失败:', e);
                    }
                }
            }
            
            message.error(errorMessage);
        } finally {
            if (shouldSetLoadingFalse) {
                setLoading(false);
            }
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setLocationType(ULD_LOCATION_TYPE.AIRPORT);
        setFlightSelectionMode('existing');
        setFlightOptions([]);
        setFlightSearchKeyword('');
        // 清理一致性错误状态
        setConsistencyError(null);
        setForceUpdate(false);
        onCancel();
    };

    // 渲染航班选择器选项
    const renderFlightOption = (flight) => {
        const flightNumber = flight.flight_number || `ID-${flight.id}`;
        const route = `${flight.departure_airport} → ${flight.arrival_airport}`;
        const aircraftReg = flight.aircraft_reg || '未分配';
        const status = flight.status;
        const statusColor = FLIGHT_STATUS_COLORS[status] || 'default';
        const statusLabel = FLIGHT_STATUS_LABELS[status] || status;
        
        // 显示计划时间或实际时间
        const departureTime = flight.etd || flight.std;
        const timeDisplay = departureTime ? dayjs(departureTime).format('MM-DD HH:mm') : '';

        return (
            <div style={{ 
                padding: '8px 12px', 
                lineHeight: '1.4',
                minHeight: '48px'
            }}>
                {/* 第一行：航班号、状态和时间 */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '4px'
                }}>
                    <Space size="small">
                        <Tag icon={<RocketOutlined />} color="blue" style={{ margin: 0 }}>
                            {flightNumber}
                        </Tag>
                        <Tag color={statusColor} style={{ margin: 0 }}>
                            {statusLabel}
                        </Tag>
                    </Space>
                    {timeDisplay && (
                        <span style={{ 
                            fontSize: '12px', 
                            color: '#999',
                            whiteSpace: 'nowrap'
                        }}>
                            {timeDisplay}
                        </span>
                    )}
                </div>
                
                {/* 第二行：航线和机型 */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center'
                }}>
                    <span style={{ 
                        fontWeight: 500, 
                        color: '#262626',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '200px'
                    }}>
                        {route}
                    </span>
                    <span style={{ 
                        fontSize: '12px', 
                        color: '#666',
                        marginLeft: '8px',
                        whiteSpace: 'nowrap'
                    }}>
                        {aircraftReg}
                    </span>
                </div>
            </div>
        );
    };

    const renderLocationFields = () => {
        switch (locationType) {
            case ULD_LOCATION_TYPE.AIRPORT:
                return (
                    <Form.Item
                        name="airport_iata"
                        label="机场IATA码"
                        rules={[{ required: true, message: "请选择机场" }]}
                    >
                        <Select
                            placeholder="请选择机场"
                            showSearch
                            loading={airportsLoading}
                            onChange={() => {
                                // 机场IATA码变化时重置一致性错误
                                if (consistencyError) {
                                    setConsistencyError(null);
                                    setForceUpdate(false);
                                }
                            }}
                            filterOption={(input, option) => {
                                const airport = (airportsList || []).find(a => a.iata_code === option.value);
                                if (!airport) return false;
                                const searchText = `${airport.iata_code} ${airport.name_cn || ''} ${airport.name_en || ''}`.toLowerCase();
                                return searchText.indexOf(input.toLowerCase()) >= 0;
                            }}
                            notFoundContent={airportsLoading ? <Spin size="small" /> : "暂无数据"}
                        >
                            {(airportsList || []).map(airport => (
                                <Option key={airport.iata_code} value={airport.iata_code}>
                                    {airport.iata_code}{(airport.name_cn || airport.name_en) ? ` - ${airport.name_cn || airport.name_en}` : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                );
            case ULD_LOCATION_TYPE.FLIGHT:
                return (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {/* 航班选择模式 */}
                        <Form.Item label="航班信息来源">
                            <Radio.Group
                                value={flightSelectionMode}
                                onChange={(e) => {
                                    setFlightSelectionMode(e.target.value);
                                    // 清空表单中的航班相关字段
                                    form.setFieldsValue({
                                        flight_op_id: undefined,
                                        transport_identifier: undefined
                                    });
                                }}
                            >
                                <Radio value="existing">选择现有航班</Radio>
                                <Radio value="manual">手动填写航班号</Radio>
                            </Radio.Group>
                        </Form.Item>

                        {flightSelectionMode === 'existing' ? (
                        <Form.Item
                            name="flight_op_id"
                                label={
                                    <Space>
                                        <span>系统航班</span>
                                        <Tooltip title="从系统中的航班数据库选择现有航班">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </Space>
                                }
                            rules={[
                                    { required: true, message: '请选择航班' }
                                ]}
                            >
                                <Select
                                    showSearch
                                    placeholder="输入航班号搜索..."
                                    suffixIcon={<SearchOutlined />}
                                    filterOption={false}
                                    onSearch={handleFlightSearch}
                                    onChange={handleExistingFlightSelect}
                                    loading={flightSearchLoading}
                                    optionLabelProp="label"
                                    notFoundContent={
                                        flightSearchLoading ? (
                                            <div style={{ textAlign: 'center', padding: '12px' }}>
                                                <Spin size="small" />
                                                <div style={{ marginTop: '8px' }}>搜索中...</div>
                                            </div>
                                        ) : flightSearchKeyword && flightSearchKeyword.length >= 2 ? (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description="未找到匹配的航班"
                                                style={{ padding: '12px' }}
                                            />
                                        ) : (
                                            <div style={{ textAlign: 'center', padding: '12px', color: '#999' }}>
                                                请输入至少2个字符搜索航班
                                            </div>
                                        )
                                    }
                                    dropdownStyle={{ 
                                        maxHeight: 400, 
                                        minWidth: 450,
                                        width: 'auto'
                                    }}
                        >
                                    {flightOptions.map((flight) => {
                                        const flightNumber = flight.flight_number || `ID-${flight.id}`;
                                        const route = `${flight.departure_airport} → ${flight.arrival_airport}`;
                                        // 增加日期显示
                                        const departureTime = flight.etd || flight.std;
                                        const dateStr = departureTime ? dayjs(departureTime).format('MM-DD') : '';
                                        const label = dateStr 
                                            ? `${flightNumber}/${dateStr} ${route}`
                                            : `${flightNumber} ${route}`;
                                        
                                        return (
                                            <Option 
                                                key={flight.id} 
                                                value={flight.id}
                                                label={label}
                                            >
                                            {renderFlightOption(flight)}
                                        </Option>
                                        );
                                    })}
                                </Select>
                        </Form.Item>
                        ) : (
                        <Form.Item
                            name="transport_identifier"
                                label={
                                    <Space>
                                        <span>外部航班号/运输标识</span>
                                        <Tooltip title="手动输入外部航班号、卡车编号等运输工具标识">
                                            <InfoCircleOutlined />
                                        </Tooltip>
                                    </Space>
                                }
                            rules={[
                                    { required: true, message: '请输入航班号或运输标识' }
                            ]}
                        >
                                <Input 
                                    placeholder="如：CA1234、TRUCK-001等" 
                                    onChange={handleManualFlightInput}
                                />
                        </Form.Item>
                        )}
                    </Space>
                );
            case ULD_LOCATION_TYPE.OTHER_LOCATION:
                return (
                    <Form.Item
                        name="new_other_location"
                        label="其他位置描述"
                        rules={[{ required: true, message: "请输入位置描述" }]}
                    >
                        <Input.TextArea 
                            placeholder="请描述具体位置" 
                            rows={3}
                            maxLength={500}
                        />
                    </Form.Item>
                );
            default:
                return null;
        }
    };

    // 【新增】强制更新位置
    const handleForceUpdate = async () => {
        let shouldSetLoadingFalse = true;
        
        try {
            // 验证表单，特别是原因字段
            const values = await form.validateFields();
            
            // 确保填写了原因
            if (!values.reason || values.reason.trim() === '') {
                message.error('强制更新时必须填写变更原因');
                return;
            }
            
            setLoading(true);
            
            // 构建请求数据，强制设置force_update为true
            const requestData = {
                location_type: locationType,
                airport_iata: values.airport_iata,
                flight_op_id: values.flight_op_id,
                transport_identifier: values.transport_identifier,
                new_other_location: values.new_other_location,
                force_update: true, // 直接设置为true，不依赖状态
                reason: buildLocationReason(values)
            };

            console.log('强制更新ULD位置:', { force_update: true, requestData });

            const response = await uldService.updateSingleUldLocation(record.master.id, requestData);
            
            // 检查HTTP状态码来判断成功/失败
            if (response.status >= 200 && response.status < 300) {
                message.success("位置强制更新成功");
                
                // 【修复】强制更新成功后清理状态
                setConsistencyError(null);
                setForceUpdate(false);
                
                onSuccess?.();
                onCancel();
            } else {
                message.error(response.data?.message || "位置更新失败");
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('请检查表单填写是否正确');
                return;
            }
            
            console.error('强制更新失败:', error);
            const errorMessage = error.response?.data?.detail || 
                               error.response?.data?.message || 
                               "强制更新失败";
            
            // 强制更新时不解析一致性错误，直接显示错误信息
            message.error(errorMessage);
        } finally {
            if (shouldSetLoadingFalse) {
                setLoading(false);
            }
        }
    };

    return (
        <Modal
            title={`更新ULD位置 - ${record?.master?.uld_identifier}`}
            open={visible}
            onCancel={handleCancel}
            footer={null} // 自定义底部按钮
            destroyOnHidden
            width={800}
        >
            <Form
                form={form}
                layout="vertical"
                preserve={false}
            >
                {/* 【新增】位置一致性校验警告 */}
                {consistencyError && (
                    <Alert
                        message="位置一致性警告"
                        description={
                            <div>
                                <p>
                                    <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                                    {consistencyError.check_type === "ULD移入航班" ? (
                                        <>
                                            ULD当前位置 <Text strong>{consistencyError.current_airport}</Text> 与航班 
                                            <Text strong> {consistencyError.flight_number}</Text> 始发站 
                                            <Text strong> {consistencyError.flight_origin}</Text> 不一致，
                                            期望位置应为 <Text strong style={{ color: '#52c41a' }}>{consistencyError.expected_airport}</Text>
                                        </>
                                    ) : (
                                        <>
                                            目标机场 <Text strong>{consistencyError.new_airport}</Text> 与航班 
                                            <Text strong> {consistencyError.flight_number}</Text> 目的站 
                                            <Text strong> {consistencyError.flight_destination}</Text> 不一致，
                                            期望位置应为 <Text strong style={{ color: '#52c41a' }}>{consistencyError.expected_airport}</Text>
                                        </>
                                    )}
                                </p>
                                <div style={{ marginTop: 12 }}>
                                    <Text strong>您可以选择：</Text>
                                    <ul style={{ marginTop: 8, marginBottom: 0 }}>
                                        <li>取消操作，修改选择的{consistencyError.check_type === "ULD移入航班" ? "航班" : "机场"}</li>
                                        <li>强制执行更新（如有特殊原因，请在备注中说明）</li>
                                    </ul>
                                </div>
                            </div>
                        }
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Form.Item
                    name="location_type"
                    label="位置类型"
                    rules={[{ required: true, message: "请选择位置类型" }]}
                >
                    <Select 
                        placeholder="请选择位置类型"
                        onChange={handleLocationTypeChange}
                    >
                        {Object.entries(ULD_LOCATION_TYPE_NAMES).map(([key, name]) => (
                            <Option key={key} value={key}>
                                {name}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                {renderLocationFields()}

                <Form.Item
                    name="reason"
                    label={consistencyError ? "补充说明（强制更新时必填）" : "补充说明"}
                    rules={consistencyError && forceUpdate ? [
                        { required: true, message: "强制更新时必须填写原因" }
                    ] : []}
                    extra="系统会自动记录位置变更方向，此处填写补充原因即可"
                >
                    <Input.TextArea 
                        placeholder={consistencyError ? "说明为什么需要强制更新..." : "如：CT158航班13JUL到达、地面代理反馈位置有误等"}
                        rows={consistencyError ? 3 : 2}
                        maxLength={USER_NOTE_MAX_LENGTH}
                    />
                </Form.Item>

                {/* 自定义底部按钮 */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    paddingTop: 16,
                    borderTop: '1px solid #f0f0f0',
                    marginTop: 16
                }}>
                    <div>
                        {consistencyError && (
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                ⚠️ 强制更新将跳过位置一致性检查，并在操作日志中标记
                            </Text>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button onClick={handleCancel}>
                            取消操作
                        </Button>
                        {consistencyError ? (
                            <Button 
                                type="primary" 
                                danger
                                onClick={handleForceUpdate}
                                loading={loading}
                            >
                                强制执行更新
                            </Button>
                        ) : (
                            <Button 
                                type="primary" 
                                onClick={handleSubmit}
                                loading={loading}
                            >
                                确认更新
                            </Button>
                        )}
                    </div>
                </div>
            </Form>
        </Modal>
    );
};

export default UldLocationUpdateModal;
